import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  InlineError,
  Input,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { EmojiButton } from '../comments/CommentInsertPickers';
import { usePasteImageUpload } from '../comments/pasteImage';
import { RichTextEditor, RichTextView, isRichTextEmpty } from '../../components/RichText';
import { useAuth } from '../auth/AuthProvider';
import { formatDate } from '../listView/TaskTable';
import { useTaskFiles, useUpdateFile } from '../files/hooks';
import { useSupplierOrganizations } from '../supplierOrganizations/hooks';
import {
  useAddReviewMember,
  useApproveReview,
  useCreateReviewComment,
  useDeleteReviewComment,
  useOpenReview,
  useRemoveReviewMember,
  useReopenReview,
  useReviewComments,
  useReviewForTask,
  useReviewMembers,
  useReviewPeople,
  useReviewStatuses,
  useSamplePhotos,
  useSubmissions,
  useSupplierCandidates,
  useUpdateReview,
  useUpdateReviewComment,
} from './hooks';
import { activeReviewStatuses, groupSubmissions, reviewErrorMessage, statusByKey } from './types';
import type { BadgeTone, SelectOption } from '../../components/ui';
import type { Task } from '../tasks/types';
import type { ReviewPerson, SupplierReviewStatus } from './types';

/**
 * The Supplier Review section of the task panel (handoff 32/33/37), TAP
 * side. Distinct but restrained (handoff 6): one section that opens the
 * branch, shows the shared brief, members, shared files, submitted samples
 * and the shared conversation, and carries the three decisions. Everything
 * a supplier will see is inside this box — nothing outside it is shared.
 *
 * Nothing here decides permission: canEdit/canManage hide controls as a
 * convenience and Row Level Security refuses anything the caller may not
 * do (proved by the Stage 1 staging assertions).
 */

const STATUS_TONES: Record<string, BadgeTone> = {
  awaiting_supplier: 'neutral',
  sample_in_progress: 'brand',
  ready_for_tap_review: 'warning',
  revision_requested: 'danger',
  approved_for_production: 'success',
  on_hold: 'neutral',
};

function personLabel(people: ReviewPerson[], userId: string | null): string {
  if (userId === null) {
    return '—';
  }
  const person = people.find((row) => row.user_id === userId);
  if (!person) {
    return 'Someone';
  }
  return person.display_name.trim() !== '' ? person.display_name : person.email;
}

function statusTone(status: SupplierReviewStatus | undefined): BadgeTone {
  return (status?.system_key && STATUS_TONES[status.system_key]) || 'neutral';
}

/** The comment box's own emoji button, on the shared conversation's toolbar. */
function emojiTool(insertText: (text: string) => void) {
  return <EmojiButton disabled={false} onPick={insertText} />;
}

/** The open-review dialog (handoff 33). */
function SendToReviewDialog({
  task,
  projectId,
  open,
  onClose,
}: {
  task: Task;
  projectId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [organizationId, setOrganizationId] = useState('');
  const [sharedTitle, setSharedTitle] = useState(task.title);
  const [instructions, setInstructions] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState('');

  const organizations = useSupplierOrganizations();
  const candidates = useSupplierCandidates(organizationId === '' ? undefined : organizationId);
  const openReview = useOpenReview();

  const organizationRows = Array.isArray(organizations.data) ? organizations.data : [];
  const organizationOptions: SelectOption[] = [
    { value: '', label: 'Choose an organization' },
    ...organizationRows
      .filter((organization) => organization.is_active)
      .map((organization) => ({ value: organization.id, label: organization.name })),
  ];

  function toggleCandidate(userId: string, checked: boolean) {
    setSelected((current) =>
      checked ? [...current, userId] : current.filter((id) => id !== userId),
    );
  }

  function submit() {
    if (organizationId === '') {
      setError('Choose a supplier organization.');
      return;
    }
    if (sharedTitle.trim() === '') {
      setError('Give the review a shared title.');
      return;
    }
    setError('');
    openReview.mutate(
      {
        taskId: task.id,
        projectId,
        supplierOrganizationId: organizationId,
        sharedTitle,
        instructions,
        reviewDueDate: dueDate === '' ? null : dueDate,
        memberUserIds: selected,
      },
      {
        onSuccess: onClose,
        onError: (cause) =>
          setError(reviewErrorMessage(cause instanceof Error ? cause.message : '')),
      },
    );
  }

  return (
    <Dialog
      open={open}
      title="Send to Supplier Review"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={openReview.isPending}>
            {openReview.isPending ? 'Opening…' : 'Open review'}
          </Button>
        </>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <Select
        label="Supplier organization"
        options={organizationOptions}
        value={organizationId}
        onChange={(event) => {
          setOrganizationId(event.target.value);
          setSelected([]);
        }}
      />
      <Input
        label="Shared title"
        value={sharedTitle}
        onChange={(event) => setSharedTitle(event.target.value)}
      />
      <Textarea
        label="Review instructions (shared brief)"
        value={instructions}
        rows={4}
        placeholder="What should the supplier check or produce? The internal task description is never shared automatically."
        onChange={(event) => setInstructions(event.target.value)}
      />
      <Input
        label="Review due date"
        type="date"
        value={dueDate}
        onChange={(event) => setDueDate(event.target.value)}
      />
      <p className="task-panel-text">Supplier users who can see this review:</p>
      {organizationId === '' ? (
        <p className="task-panel-text">Choose an organization first.</p>
      ) : candidates.isPending ? (
        <Skeleton lines={2} label="Loading supplier users" />
      ) : (candidates.data ?? []).length === 0 ? (
        <p className="task-panel-text">This organization has no active supplier users yet.</p>
      ) : (
        (candidates.data ?? []).map((candidate) => (
          <Checkbox
            key={candidate.user_id}
            label={candidate.display_name.trim() !== '' ? candidate.display_name : candidate.email}
            checked={selected.includes(candidate.user_id)}
            onChange={(event) => toggleCandidate(candidate.user_id, event.target.checked)}
          />
        ))
      )}
    </Dialog>
  );
}

export function SupplierReviewSection({
  task,
  projectId,
  canEdit,
  canManage,
  heading = 'Supplier Review',
}: {
  task: Task;
  projectId: string;
  canEdit: boolean;
  canManage: boolean;
  /** Empty when the container already names the section (the task panel's fold). */
  heading?: string;
}) {
  const pasteImage = usePasteImageUpload();
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState<'approve' | 'revision' | null>(null);
  const [approveFileId, setApproveFileId] = useState('');
  const [approveSubmissionId, setApproveSubmissionId] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const [revisionComment, setRevisionComment] = useState('');
  const [confirmingReopen, setConfirmingReopen] = useState(false);
  const [memberToAdd, setMemberToAdd] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  // A pasted screenshot still uploading: Send and Save wait for it (design note S16).
  const [uploading, setUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  const { access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  const reviewQuery = useReviewForTask(task.id);
  const review = reviewQuery.data ?? null;
  const reviewId = review?.id;

  const statuses = useReviewStatuses();
  const people = useReviewPeople(reviewId);
  const members = useReviewMembers(reviewId);
  const candidates = useSupplierCandidates(review?.supplier_organization_id);
  const submissions = useSubmissions(reviewId);
  const photos = useSamplePhotos(reviewId);
  const comments = useReviewComments(reviewId);
  const taskFiles = useTaskFiles(task.id);

  const updateReview = useUpdateReview();
  const approveReview = useApproveReview();
  const reopenReview = useReopenReview();
  const addMember = useAddReviewMember();
  const removeMember = useRemoveReviewMember(reviewId ?? '');
  const createComment = useCreateReviewComment(reviewId ?? '', projectId);
  const updateComment = useUpdateReviewComment(reviewId ?? '');
  const deleteComment = useDeleteReviewComment(reviewId ?? '');
  const updateFile = useUpdateFile(task.id);

  const closeSend = useCallback(() => setSending(false), []);
  const closeDecision = useCallback(() => setDeciding(null), []);

  const statusList = statuses.data ?? [];
  const peopleList = useMemo(() => people.data ?? [], [people.data]);
  const currentStatus = statusList.find((row) => row.id === review?.status_id);
  const isApproved = review !== null && review.approved_at !== null;

  const submittedVersions = groupSubmissions(
    (submissions.data ?? []).filter((submission) => submission.is_submitted),
    photos.data ?? [],
  );
  const latestFiles = (taskFiles.data ?? []).filter((file) => file.is_latest_version);
  const sharedFiles = latestFiles.filter((file) => file.is_supplier_visible);

  function report(cause: unknown) {
    setError(reviewErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function setWorkflowStatus(statusId: string) {
    if (!review) {
      return;
    }
    setError('');
    updateReview.mutate(
      { reviewId: review.id, changes: { status_id: statusId } },
      { onError: report },
    );
  }

  function submitApproval() {
    if (!review) {
      return;
    }
    const approvedStatus = statusByKey(statusList, 'approved_for_production');
    if (!approvedStatus) {
      setError('The Approved for Production status is missing.');
      return;
    }
    setError('');
    approveReview.mutate(
      {
        reviewId: review.id,
        approvedDesignFileId: approveFileId === '' ? null : approveFileId,
        approvedSampleSubmissionId: approveSubmissionId === '' ? null : approveSubmissionId,
        approvalNote: approveNote,
        approvedStatusId: approvedStatus.id,
      },
      { onSuccess: closeDecision, onError: report },
    );
  }

  function submitRevision() {
    if (!review) {
      return;
    }
    if (revisionComment.trim() === '') {
      setError('A revision request needs a comment (handoff 37).');
      return;
    }
    const revisionStatus = statusByKey(statusList, 'revision_requested');
    if (!revisionStatus) {
      setError('The Revision Requested status is missing.');
      return;
    }
    setError('');
    createComment.mutate(revisionComment, {
      onSuccess: () => {
        updateReview.mutate(
          { reviewId: review.id, changes: { status_id: revisionStatus.id } },
          { onSuccess: closeDecision, onError: report },
        );
        setRevisionComment('');
      },
      onError: report,
    });
  }

  function putOnHold() {
    const holdStatus = statusByKey(statusList, 'on_hold');
    if (holdStatus) {
      setWorkflowStatus(holdStatus.id);
    }
  }

  function reopen() {
    if (!review) {
      return;
    }
    const revisionStatus = statusByKey(statusList, 'revision_requested');
    if (!revisionStatus) {
      return;
    }
    setError('');
    setConfirmingReopen(false);
    reopenReview.mutate(
      { reviewId: review.id, nextStatusId: revisionStatus.id },
      { onError: report },
    );
  }

  function addSelectedMember() {
    if (!review || memberToAdd === '') {
      return;
    }
    setError('');
    addMember.mutate(
      {
        reviewId: review.id,
        supplierOrganizationId: review.supplier_organization_id,
        userId: memberToAdd,
      },
      { onSuccess: () => setMemberToAdd(''), onError: report },
    );
  }

  function postComment() {
    if (uploading) {
      return;
    }
    if (isRichTextEmpty(commentBody)) {
      setError('Type a message first.');
      return;
    }
    setError('');
    createComment.mutate(commentBody, { onSuccess: () => setCommentBody(''), onError: report });
  }

  function saveCommentEdit() {
    if (editUploading || editingCommentId === null || isRichTextEmpty(editingBody)) {
      return;
    }
    setError('');
    updateComment.mutate(
      { commentId: editingCommentId, body: editingBody },
      { onSuccess: () => setEditingCommentId(null), onError: report },
    );
  }

  const memberRows = members.data ?? [];
  const availableCandidates = (candidates.data ?? []).filter(
    (candidate) => !memberRows.some((member) => member.user_id === candidate.user_id),
  );

  const workflowOptions: SelectOption[] = activeReviewStatuses(statusList)
    .filter((row) => row.system_key !== 'approved_for_production')
    .map((row) => ({ value: row.id, label: row.name }));

  return (
    <section className="supplier-review-section">
      {heading === '' ? null : <h3 className="task-panel-heading">{heading}</h3>}
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {reviewQuery.isPending ? (
        <Skeleton lines={2} label="Loading supplier review" />
      ) : reviewQuery.isError ? (
        <p className="task-panel-text">The supplier review could not be loaded.</p>
      ) : review === null ? (
        <>
          <p className="task-panel-text">This task has not been sent to a supplier.</p>
          {canEdit ? (
            <Button variant="secondary" onClick={() => setSending(true)}>
              Send to Supplier Review
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <div className="review-facts">
            <Badge tone={statusTone(currentStatus)}>{currentStatus?.name ?? 'Unknown'}</Badge>
            <span className="review-fact">
              Due {review.review_due_date ? formatDate(review.review_due_date) : '—'}
            </span>
            <span className="review-fact">
              Opened by {personLabel(peopleList, review.opened_by)}
            </span>
          </div>

          {isApproved ? (
            <div className="review-approval">
              <Badge tone="success">Approved for Production</Badge>
              <p className="task-panel-text">
                Approved by {personLabel(peopleList, review.approved_by)} on{' '}
                {formatDate(review.approved_at)}
                {review.approval_note ? ` — ${review.approval_note}` : ''}
              </p>
              {canManage ? (
                confirmingReopen ? (
                  <span className="review-actions">
                    <Button variant="ghost" size="sm" onClick={() => setConfirmingReopen(false)}>
                      Keep approval
                    </Button>
                    <Button variant="danger" size="sm" onClick={reopen}>
                      Confirm reopen
                    </Button>
                  </span>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingReopen(true)}>
                    Reopen review
                  </Button>
                )
              ) : null}
            </div>
          ) : (
            <div className="review-actions">
              {canEdit ? (
                <Select
                  label="Workflow status"
                  options={workflowOptions}
                  value={review.status_id}
                  onChange={(event) => setWorkflowStatus(event.target.value)}
                />
              ) : null}
              {canManage ? (
                <>
                  <Button size="sm" onClick={() => setDeciding('approve')}>
                    Approve for Production
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setDeciding('revision')}>
                    Request Revision
                  </Button>
                  <Button variant="ghost" size="sm" onClick={putOnHold}>
                    On Hold
                  </Button>
                </>
              ) : null}
            </div>
          )}

          {review.instructions === '' ? null : (
            <>
              <h4 className="review-subheading">Shared brief</h4>
              <p className="task-panel-text">{review.instructions}</p>
            </>
          )}

          <h4 className="review-subheading">Supplier users</h4>
          {memberRows.length === 0 ? (
            <p className="task-panel-text">No supplier users selected yet.</p>
          ) : (
            <ul className="review-list">
              {memberRows.map((member) => (
                <li key={member.id} className="review-row">
                  <span>{personLabel(peopleList, member.user_id)}</span>
                  {canEdit ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${personLabel(peopleList, member.user_id)} from the review`}
                      onClick={() => removeMember.mutate(member.id, { onError: report })}
                    >
                      Remove
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {canEdit && availableCandidates.length > 0 ? (
            <div className="review-add-member">
              <Select
                label="Add supplier user"
                value={memberToAdd}
                onChange={(event) => setMemberToAdd(event.target.value)}
                options={[
                  { value: '', label: 'Choose a supplier user' },
                  ...availableCandidates.map((candidate) => ({
                    value: candidate.user_id,
                    label:
                      candidate.display_name.trim() !== ''
                        ? candidate.display_name
                        : candidate.email,
                  })),
                ]}
              />
              <Button size="sm" onClick={addSelectedMember} disabled={addMember.isPending}>
                {addMember.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          ) : null}

          <h4 className="review-subheading">Shared files</h4>
          {latestFiles.length === 0 ? (
            <p className="task-panel-text">Attach files to the task first, then share them.</p>
          ) : (
            <ul className="review-list">
              {latestFiles.map((file) => (
                <li key={file.id} className="review-row">
                  <a href={file.url} target="_blank" rel="noreferrer" className="file-name">
                    {file.name}
                  </a>
                  {canEdit ? (
                    <Checkbox
                      label="Shared"
                      checked={file.is_supplier_visible}
                      onChange={(event) => {
                        setError('');
                        updateFile.mutate(
                          {
                            fileId: file.id,
                            changes: { is_supplier_visible: event.target.checked },
                          },
                          { onError: report },
                        );
                      }}
                    />
                  ) : file.is_supplier_visible ? (
                    <Badge tone="brand">Shared</Badge>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          <h4 className="review-subheading">Sample submissions</h4>
          {submittedVersions.length === 0 ? (
            <p className="task-panel-text">No samples submitted yet.</p>
          ) : (
            <ul className="review-list">
              {submittedVersions.map(({ submission, photos: versionPhotos }) => (
                <li key={submission.id} className="review-sample">
                  <div className="review-row">
                    <strong>Sample V{submission.version_number}</strong>
                    <span className="review-fact">
                      Submitted {formatDate(submission.submitted_at)} by{' '}
                      {personLabel(peopleList, submission.created_by)}
                    </span>
                  </div>
                  {submission.note === '' ? null : (
                    <p className="task-panel-text">{submission.note}</p>
                  )}
                  {versionPhotos.length === 0 ? null : (
                    <ul className="review-photo-list">
                      {versionPhotos.map((photo) => (
                        <li key={photo.id}>
                          <a
                            href={photo.url}
                            target="_blank"
                            rel="noreferrer"
                            className="file-name"
                          >
                            {photo.label !== '' ? `${photo.label} — ${photo.name}` : photo.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}

          <h4 className="review-subheading">Shared conversation</h4>
          <p className="review-shared-note">Everything below is visible to the supplier.</p>
          {(comments.data ?? []).length === 0 ? (
            <p className="task-panel-text">No shared messages yet.</p>
          ) : (
            <ul className="review-list">
              {(comments.data ?? []).map((comment) => (
                <li key={comment.id} className="review-comment">
                  <div className="review-row">
                    <strong>{personLabel(peopleList, comment.author_id)}</strong>
                    <span className="review-fact">
                      {formatDate(comment.created_at)}
                      {comment.edited_at ? ' · edited' : ''}
                    </span>
                  </div>
                  {editingCommentId === comment.id ? (
                    <>
                      <RichTextEditor
                        label="Edit message"
                        value={editingBody}
                        onChange={setEditingBody}
                        onPasteImage={pasteImage}
                        onUploadingChange={setEditUploading}
                        extraTools={emojiTool}
                      />
                      <span className="review-actions">
                        <Button size="sm" onClick={saveCommentEdit} disabled={editUploading}>
                          Save
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingCommentId(null)}>
                          Cancel
                        </Button>
                      </span>
                    </>
                  ) : (
                    <RichTextView value={comment.body} />
                  )}
                  <span className="review-actions">
                    {comment.author_id === myId && editingCommentId !== comment.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Edit message from ${formatDate(comment.created_at)}`}
                        onClick={() => {
                          setEditingCommentId(comment.id);
                          setEditingBody(comment.body);
                        }}
                      >
                        Edit
                      </Button>
                    ) : null}
                    {comment.author_id === myId || canManage ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete message from ${formatDate(comment.created_at)}`}
                        onClick={() => deleteComment.mutate(comment.id, { onError: report })}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {canEdit ? (
            <div className="review-composer">
              <RichTextEditor
                label="Message the supplier"
                value={commentBody}
                onChange={setCommentBody}
                placeholder="Visible to the supplier"
                onPasteImage={pasteImage}
                onUploadingChange={setUploading}
                extraTools={emojiTool}
              />
              <Button
                size="sm"
                onClick={postComment}
                disabled={createComment.isPending || uploading}
              >
                {createComment.isPending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <SendToReviewDialog task={task} projectId={projectId} open={sending} onClose={closeSend} />

      <Dialog
        open={deciding === 'approve'}
        title="Approve for Production"
        onClose={closeDecision}
        footer={
          <>
            <Button variant="ghost" onClick={closeDecision}>
              Cancel
            </Button>
            <Button onClick={submitApproval} disabled={approveReview.isPending}>
              {approveReview.isPending ? 'Approving…' : 'Confirm approval'}
            </Button>
          </>
        }
      >
        <p className="task-panel-text">
          This records a permanent approval and authorizes production (handoff 37).
        </p>
        <Select
          label="Approved design file"
          value={approveFileId}
          onChange={(event) => setApproveFileId(event.target.value)}
          options={[
            { value: '', label: 'None' },
            ...sharedFiles.map((file) => ({ value: file.id, label: file.name })),
          ]}
        />
        <Select
          label="Approved sample version"
          value={approveSubmissionId}
          onChange={(event) => setApproveSubmissionId(event.target.value)}
          options={[
            { value: '', label: 'None' },
            ...submittedVersions.map(({ submission }) => ({
              value: submission.id,
              label: `Sample V${submission.version_number}`,
            })),
          ]}
        />
        <Textarea
          label="Approval note"
          value={approveNote}
          rows={2}
          onChange={(event) => setApproveNote(event.target.value)}
        />
      </Dialog>

      <Dialog
        open={deciding === 'revision'}
        title="Request Revision"
        onClose={closeDecision}
        footer={
          <>
            <Button variant="ghost" onClick={closeDecision}>
              Cancel
            </Button>
            <Button onClick={submitRevision} disabled={createComment.isPending}>
              {createComment.isPending ? 'Sending…' : 'Request revision'}
            </Button>
          </>
        }
      >
        <Textarea
          label="What needs to change (required, shared with the supplier)"
          value={revisionComment}
          rows={3}
          onChange={(event) => setRevisionComment(event.target.value)}
        />
      </Dialog>
    </section>
  );
}
